import { useSelector } from 'react-redux';

import { liveCheckSelectors } from 'wanderu-ducks-live-check';
import { tripSummarySelectors } from 'wanderu-ducks-search';
import { tripSelectors } from 'wanderu-ducks-trips';
import type { RootState } from 'wanderu-shellapp';

import { formatPricing } from './utils';

import type { I18nMoneyFunction, Trip } from '../types';

const usePricesSelector = ({
    seats,
    showTotalPrice,
    tripIds,
    wheelchairs,
}: {
    seats: number,
    showTotalPrice: boolean
    tripIds: string[],
    wheelchairs: number,
}) => {
    const pricesSelector = (state: RootState) => tripSummarySelectors
        .selectPrices(
            state,
            tripIds,
            seats,
            wheelchairs,
            showTotalPrice
        );

    return useSelector(pricesSelector);
};

const useTripSetSelector = (tripId: string) => {
    const tripSetSelector = (state: RootState) => tripSelectors.selectTripSet(state, tripId);

    return useSelector(tripSetSelector);
};

const useTripLiveCheckSelector = ({
    seats,
    tripId,
    wheelchairs,
}: {
    seats: number,
    tripId: string,
    wheelchairs: number,
}) => {
    const lcParams = {
        fareVersion: 'v2',
        seats,
        tripId,
        wheelchairs,
    };

    const tripLiveCheckSelector = (state: RootState) => {
        if (!tripId) {
            return null;
        }

        const {
            tripData,
        } = liveCheckSelectors.selectTripLiveCheck(state, lcParams) || {};

        return tripData;
    };

    return useSelector(tripLiveCheckSelector);
};

const useCarrierDataSelector = (tripId: string) => {
    const carrierDataSelector = (state: RootState) => tripSelectors
        .selectTripCarrierData(state, tripId);

    return useSelector(carrierDataSelector);
};

const useTripDepartDataSelector = (tripId: string) => {
    const tripDepartDataSelector = (state: RootState) => tripSelectors
        .selectTripDepartData(state, tripId);

    return useSelector(tripDepartDataSelector);
};

const useTripArriveDataSelector = (tripId: string) => {
    const tripArriveDataSelector = (state: RootState) => tripSelectors
        .selectTripArriveData(state, tripId);

    return useSelector(tripArriveDataSelector);
};

const useSelectTripData = (tripId: string) => {
    const tripData = (state: RootState) => tripSelectors.selectTripData(state, tripId) || {};

    return useSelector(tripData);
};

const useTripPricesSelector = ({
    seats,
    selectedFare,
    tripId,
    wheelchairs,
}: {
    seats: number,
    selectedFare: string,
    tripId: string,
    wheelchairs: number,
}) => {
    const tripPricesSelector = (state: RootState) => liveCheckSelectors
        .selectLiveCheckPrice(state, {
            seats,
            selectedFare,
            tripId,
            wheelchairs,
        }) || {};

    return useSelector(tripPricesSelector);
};

const useItineraryInfoSelector = (itineraryId: string) => {
    const [outboundTripId, returnTripId] = itineraryId.split('||');
    const outboundTrips = useTripSetSelector(outboundTripId) || [];
    const returnTrips = useTripSetSelector(returnTripId) || [];

    const outboundTripIds = outboundTrips.map((trip: Trip) => trip.tripId);
    const returnTripIds = returnTrips.map((trip: Trip) => trip.tripId);
    const tripIds = [...outboundTripIds, ...returnTripIds];

    const {
        seats = 0,
        wheelchairs = 0,
    } = useSelector(tripSummarySelectors.selectTravelerInfo);

    const tripPricing = usePricesSelector({
        seats,
        showTotalPrice: true,
        tripIds,
        wheelchairs,
    });

    return {
        outboundTripId,
        outboundTrips,
        returnTripId,
        returnTrips,
        tripPricing,
    };
};

const useFormattedTripPricingSelector = ({
    i18nMoney,
    tripId,
}: {
    i18nMoney: I18nMoneyFunction,
    tripId: string,
}) => {
    const {
        seats = 0,
        wheelchairs = 0,
    } = useSelector(tripSummarySelectors.selectTravelerInfo);

    const { selectedFare } = useSelectTripData(tripId);

    const priceObject = useTripPricesSelector({
        seats,
        selectedFare,
        tripId,
        wheelchairs,
    });

    const pricing = formatPricing({ i18nMoney, priceObject });

    return pricing;
};

const useFormattedTripsSelector = ({
    i18nMoney,
    trips,
}: {
    i18nMoney: I18nMoneyFunction,
    trips: Trip[],
}) => {
    const useFormatTrip = (trip: Trip) => {
        const {
            arriveName,
            carrierCode,
            departName,
            isRouted,
            tripId = '',
        } = trip;

        const {
            carrierFees,
            taxes,
        } = useFormattedTripPricingSelector({ i18nMoney, tripId });

        return {
            arriveName,
            carrierCode,
            carrierFees,
            departName,
            isRouted,
            taxes,
            tripId,
        };
    };

    const formattedTrips = trips.map(useFormatTrip);

    return formattedTrips;
};

export {
    useCarrierDataSelector,
    useFormattedTripPricingSelector,
    useFormattedTripsSelector,
    useItineraryInfoSelector,
    usePricesSelector,
    useSelectTripData,
    useTripArriveDataSelector,
    useTripDepartDataSelector,
    useTripLiveCheckSelector,
    useTripPricesSelector,
    useTripSetSelector,
};
